import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Title } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Chip,
  TablePagination,
  TextField,
  Button,
  Alert,
  IconButton,
} from "@mui/material";
import { ArrowBack, Search } from "@mui/icons-material";
import { StatusChip } from "../../../../components/StatusChip";
import { CopyableId } from "../../../../components/CopyableId";
import { API, fetchJson } from "../../api";

interface FdbBet {
  betId: string;
  operatorId: string;
  userId: string;
  username: string;
  slot: number;
  roundId: string;
  status: string;
  betAmount: number;
  betCurrency: string;
  operatorSessionId: string;
  payoutMultiplier: number | null;
  payoutAmount: number | null;
  payoutCurrency: string | null;
  autoCashoutMultiplier: number | null;
  walletTransactionId: string | null;
  createdAt: string;
}

interface FdbRound {
  roundId: string;
  state: string;
  crashPoint: number;
  currentMultiplier: number;
  serverSeed: string;
  fairnessHash: string;
  initializedAt: string;
  startedAt: string | null;
  bettingClosingStartedAt: string | null;
  flyingStartedAt: string | null;
  crashedAt: string | null;
  markedForOutbox: boolean;
  bettingOpenDurationMillis: number | null;
  bettingClosingDurationMillis: number | null;
  crashedDurationMillis: number | null;
}

interface Autocashout {
  betId: string;
  multiplier: number;
}

interface SpringPage<T> {
  content: T[];
  totalElements: number;
}

export const FdbRoundShow = () => {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [round, setRound] = useState<FdbRound | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [bets, setBets] = useState<FdbBet[]>([]);
  const [betTotal, setBetTotal] = useState(0);
  const [betPage, setBetPage] = useState(0);
  const [activeBets, setActiveBets] = useState<string[]>([]);
  const [autocashouts, setAutocashouts] = useState<Autocashout[]>([]);
  const [outbox, setOutbox] = useState<string[]>([]);
  const [userBetIds, setUserBetIds] = useState<string[]>([]);
  const [userIdSearch, setUserIdSearch] = useState("");
  const [userBetSearching, setUserBetSearching] = useState(false);
  const [userBetError, setUserBetError] = useState<string | null>(null);
  const [betIdSearch, setBetIdSearch] = useState("");
  const [selectedBet, setSelectedBet] = useState<FdbBet | null>(null);
  const [betSearching, setBetSearching] = useState(false);
  const [betSearchError, setBetSearchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState("");

  // Load round data
  useEffect(() => {
    if (!roundId) return;
    setLoading(true);
    setNotFound(false);
    setRound(null);
    setBets([]);
    setBetTotal(0);
    setBetPage(0);
    setActiveBets([]);
    setAutocashouts([]);
    setOutbox([]);
    setUserBetIds([]);
    setUserIdSearch("");
    setUserBetError(null);
    setBetIdSearch("");
    setSelectedBet(null);
    setBetSearchError(null);
    setTab(0);

    fetchJson<FdbRound>(`${API}/fdb/rounds/${roundId}`)
      .then((data) => {
        if (data) setRound(data);
        else setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [roundId]);

  const loadBets = useCallback(
    async (page: number) => {
      if (!roundId) return;
      const data = await fetchJson<SpringPage<FdbBet>>(
        `${API}/fdb/rounds/${roundId}/bets?page=${page}&size=25`,
      );
      if (data) {
        setBets(data.content);
        setBetTotal(data.totalElements);
      }
    },
    [roundId],
  );

  useEffect(() => {
    if (tab === 0 && round) loadBets(betPage);
  }, [tab, betPage, loadBets, round]);

  useEffect(() => {
    if (!roundId || !round) return;
    if (tab === 1) {
      fetchJson<string[]>(`${API}/fdb/rounds/${roundId}/active-bets`).then(
        (d) => setActiveBets(d ?? []),
      );
    } else if (tab === 2) {
      fetchJson<Autocashout[]>(
        `${API}/fdb/rounds/${roundId}/autocashouts`,
      ).then((d) => setAutocashouts(d ?? []));
    } else if (tab === 3) {
      fetchJson<string[]>(`${API}/fdb/rounds/${roundId}/outbox`).then((d) =>
        setOutbox(d ?? []),
      );
    }
  }, [tab, roundId, round]);

  const handleUserBetSearch = async () => {
    const uid = userIdSearch.trim();
    if (!uid || !roundId) return;
    setUserBetError(null);
    setUserBetIds([]);
    setUserBetSearching(true);
    try {
      const data = await fetchJson<string[]>(
        `${API}/fdb/rounds/${roundId}/users/${uid}/bets`,
      );
      if (data && data.length > 0) {
        setUserBetIds(data);
      } else {
        setUserBetError(`No bets found for user "${uid}" in this round`);
      }
    } finally {
      setUserBetSearching(false);
    }
  };

  const handleBetIdSearch = async () => {
    const bid = betIdSearch.trim();
    if (!bid || !roundId) return;
    setBetSearchError(null);
    setSelectedBet(null);
    setBetSearching(true);
    try {
      const data = await fetchJson<FdbBet>(
        `${API}/fdb/rounds/${roundId}/bets/${bid}`,
      );
      if (data) {
        setSelectedBet(data);
      } else {
        setBetSearchError(`Bet "${bid}" not found in this round`);
      }
    } finally {
      setBetSearching(false);
    }
  };

  const handleSearch = () => {
    const id = searchId.trim();
    if (id && id !== roundId) {
      navigate(`/crash/fdb/rounds/${id}`);
      setSearchId("");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title={`FDB Round ${roundId}`} />

      {/* Navigation bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <IconButton
          size="small"
          onClick={() => navigate("/crash/fdb")}
          sx={{ color: "#94a3b8" }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="body2" sx={{ color: "#94a3b8", mr: 2 }}>
          FDB Round Browser
        </Typography>
        <TextField
          placeholder="Jump to Round ID..."
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            sx: {
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.85rem",
            },
          }}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleSearch}
          disabled={!searchId.trim()}
          startIcon={<Search />}
        >
          Go
        </Button>
      </Box>

      {notFound && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Round <b>{roundId}</b> not found in FDB cache. It may have been
          evicted or never existed.
        </Alert>
      )}

      {round && (
        <>
          {/* Round header card */}
          <Card
            sx={{ mb: 2, bgcolor: "#1e293b", borderLeft: "3px solid #7c3aed" }}
          >
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Chip
                  label={round.roundId}
                  size="small"
                  sx={{ fontFamily: "'JetBrains Mono', monospace" }}
                />
                <StatusChip value={round.state} />
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  Crash: <b style={{ color: "#f87171" }}>{round.crashPoint}x</b>
                </Typography>
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  Current:{" "}
                  <b style={{ color: "#34d399" }}>{round.currentMultiplier}x</b>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Seed:{" "}
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {round.serverSeed}
                  </span>
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Init: {round.initializedAt}
                </Typography>
                {round.startedAt && (
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Started: {round.startedAt}
                  </Typography>
                )}
                {round.bettingClosingStartedAt && (
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Betting Closing: {round.bettingClosingStartedAt}
                  </Typography>
                )}
                {round.flyingStartedAt && (
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Flying: {round.flyingStartedAt}
                  </Typography>
                )}
                {round.crashedAt && (
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Crashed: {round.crashedAt}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={`Bets (${betTotal})`} />
            <Tab label={`Active (${activeBets.length})`} />
            <Tab label={`Autocashouts (${autocashouts.length})`} />
            <Tab label={`Outbox (${outbox.length})`} />
            <Tab label="User Bets" />
          </Tabs>

          {tab === 0 && (
            <>
              <Box
                sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}
              >
                <TextField
                  placeholder="Look up Bet ID..."
                  value={betIdSearch}
                  onChange={(e) => {
                    setBetIdSearch(e.target.value);
                    setBetSearchError(null);
                    setSelectedBet(null);
                  }}
                  size="small"
                  sx={{ width: 360 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "0.85rem",
                    },
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleBetIdSearch()}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleBetIdSearch}
                  disabled={betSearching || !betIdSearch.trim()}
                  startIcon={
                    betSearching ? <CircularProgress size={16} /> : <Search />
                  }
                >
                  Find
                </Button>
              </Box>
              {betSearchError && (
                <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
                  {betSearchError}
                </Alert>
              )}
              {selectedBet && (
                <Card
                  sx={{
                    mb: 2,
                    bgcolor: "#1e293b",
                    border: "1px solid #7c3aed",
                  }}
                >
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography
                      variant="overline"
                      sx={{ color: "#7c3aed", fontSize: "0.65rem" }}
                    >
                      Bet Detail
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        flexWrap: "wrap",
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        ID: <CopyableId value={selectedBet.betId} />
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        User: <CopyableId value={selectedBet.userId} />
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Operator: <b>{selectedBet.operatorId}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Status: <StatusChip value={selectedBet.status} />
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Amount:{" "}
                        <b>
                          {selectedBet.betAmount} {selectedBet.betCurrency}
                        </b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Payout:{" "}
                        <b>
                          {selectedBet.payoutAmount ?? "—"}{" "}
                          {selectedBet.payoutCurrency ?? ""}
                        </b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Payout x: <b>{selectedBet.payoutMultiplier ?? "—"}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Auto x:{" "}
                        <b>{selectedBet.autoCashoutMultiplier ?? "—"}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        Slot: <b>{selectedBet.slot}</b>
                      </Typography>
                    </Box>
                    {selectedBet.walletTransactionId && (
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", mt: 0.5, display: "block" }}
                      >
                        Wallet Tx:{" "}
                        <CopyableId value={selectedBet.walletTransactionId} />
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Bet ID</TableCell>
                    <TableCell>Operator</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Cur</TableCell>
                    <TableCell align="right">Payout x</TableCell>
                    <TableCell align="right">Payout</TableCell>
                    <TableCell align="right">Auto x</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bets.map((b) => (
                    <TableRow key={b.betId}>
                      <TableCell>
                        <CopyableId value={b.betId} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>
                        {b.operatorId}
                      </TableCell>
                      <TableCell>
                        <CopyableId value={b.userId} />
                      </TableCell>
                      <TableCell>
                        <StatusChip value={b.status} />
                      </TableCell>
                      <TableCell align="right">{b.betAmount}</TableCell>
                      <TableCell>{b.betCurrency}</TableCell>
                      <TableCell align="right">
                        {b.payoutMultiplier ?? "—"}
                      </TableCell>
                      <TableCell align="right">
                        {b.payoutAmount ?? "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fbbf24" }}>
                        {b.autoCashoutMultiplier
                          ? `${b.autoCashoutMultiplier}x`
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {b.createdAt}
                      </TableCell>
                    </TableRow>
                  ))}
                  {bets.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{ color: "#94a3b8" }}
                      >
                        No bets in this round
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={betTotal}
                page={betPage}
                onPageChange={(_, p) => setBetPage(p)}
                rowsPerPage={25}
                rowsPerPageOptions={[25]}
              />
            </>
          )}

          {tab === 1 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {activeBets.length === 0 && (
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  No active bets
                </Typography>
              )}
              {activeBets.map((id) => (
                <Box key={id} sx={{ display: "inline-block", mr: 1, mb: 0.5 }}>
                  <CopyableId value={id} />
                </Box>
              ))}
            </Box>
          )}

          {tab === 2 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bet ID</TableCell>
                  <TableCell align="right">Multiplier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {autocashouts.map((ac) => (
                  <TableRow key={ac.betId}>
                    <TableCell>
                      <CopyableId value={ac.betId} />
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#fbbf24" }}>
                      {ac.multiplier}x
                    </TableCell>
                  </TableRow>
                ))}
                {autocashouts.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ color: "#94a3b8" }}
                    >
                      No autocashout entries
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {tab === 3 && (
            <>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                Composite keys (roundId:betId:action) in outbox range for
                diagnosing stuck settlements.
              </Typography>
              {outbox.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  No outbox entries
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Composite Key</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outbox.map((key) => (
                      <TableRow key={key}>
                        <TableCell>
                          <CopyableId value={key} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}

          {tab === 4 && (
            <>
              <Box
                sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}
              >
                <TextField
                  placeholder="Enter User ID..."
                  value={userIdSearch}
                  onChange={(e) => {
                    setUserIdSearch(e.target.value);
                    setUserBetError(null);
                    setUserBetIds([]);
                  }}
                  size="small"
                  sx={{ width: 360 }}
                  InputProps={{
                    sx: {
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "0.85rem",
                    },
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleUserBetSearch()}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUserBetSearch}
                  disabled={userBetSearching || !userIdSearch.trim()}
                  startIcon={
                    userBetSearching ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Search />
                    )
                  }
                >
                  Search
                </Button>
              </Box>
              {userBetError && (
                <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
                  {userBetError}
                </Alert>
              )}
              {userBetIds.length > 0 && (
                <>
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      mb: 1,
                      display: "block",
                    }}
                  >
                    {userBetIds.length} bet(s) found
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {userBetIds.map((id) => (
                      <Box
                        key={id}
                        sx={{ display: "inline-block", mr: 1, mb: 0.5 }}
                      >
                        <CopyableId value={id} />
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};
